import { createAuthenticatedFetch, type AssetExistsResponse } from './types/core/common';
import type { DigitalEntity, DigitalEntityCreate, DigitalEntityWithMemos } from './types/entities/digitalEntity/types';

// Interface pour les erreurs API
interface ApiError {
  status?: number;
  message?: string;
}

// @deprecated Use imports from '@/api/types' instead
export type {
    DigitalEntity,
    DigitalEntityCreate,
    DigitalEntityWithMemos
};

const api = createAuthenticatedFetch();

// Objet principal contenant toutes les fonctions
export const digitalEntities = {
  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================
  
  async fetchAllDigitalEntities(): Promise<DigitalEntity[]> {
    try {
      return await api.get('/digitalentity/admin/all');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des entités numériques");
    }
  },

  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les entités numériques de l'utilisateur connecté
   */
  async getMy(): Promise<DigitalEntity[]> {
    return api.get<DigitalEntity[]>('/digitalentity/my');
  },

  /**
   * Récupère les entités numériques publiques seulement
   */
  async getPublic(): Promise<DigitalEntity[]> {
    return api.get<DigitalEntity[]>('/digitalentity/public');
  },

  /**
   * Récupère les entités numériques accessibles (utilisateur + publiques)
   */
  async getAccessible(): Promise<DigitalEntity[]> {
    return api.get<DigitalEntity[]>('/digitalentity/accessible');
  },

  /**
   * Récupère toutes les entités numériques (admin seulement)
   */
  async getAllAdmin(): Promise<DigitalEntity[]> {
    return api.get<DigitalEntity[]>('/digitalentity/all');
  },

  /**
   * Récupère toutes les entités numériques (admin seulement), y compris soft-deleted
   */
  async getAllAdminWithDeleted(): Promise<DigitalEntity[]> {
    return api.get<DigitalEntity[]>('/digitalentity/all_with_deleted');
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  async createDigitalEntity(entity: DigitalEntityCreate): Promise<DigitalEntity> {
    return api.post<DigitalEntity>('/digitalentity/', entity);
  },

  async updateDigitalEntity(uuid: string, entity: Partial<DigitalEntityCreate>): Promise<DigitalEntity> {
    return api.put<DigitalEntity>(`/digitalentity/${uuid}`, entity);
  },

  async deleteDigitalEntity(uuid: string): Promise<void> {
    return api.delete(`/digitalentity/${uuid}`);
  },

  async hardDeleteDigitalEntity(uuid: string): Promise<void> {
    return api.delete(`/digitalentity/hard_delete/${uuid}`);
  },
  async restoreDigitalEntity(uuid: string): Promise<void> {
    return api.put(`/digitalentity/restore/${uuid}`);
  },

  async fetchDigitalEntityWithMemos(uuid: string): Promise<DigitalEntityWithMemos> {
    try {
      return await api.get(`/digitalentity/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité numérique non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité numérique et ses mémos");
    }
  },

  async fetchDigitalEntitiesByUser(userId: string): Promise<DigitalEntity[]> {
    try {
      return await api.get(`/digitalentity/user/${userId}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération des entités numériques de l'utilisateur");
    }
  },

  /**
   * Récupère une entité digitale publique par UUID (accès public)
   */
  async getPublicByUuid(uuid: string): Promise<DigitalEntity> {
    try {
      return await api.get(`/digitalentity/public/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité digitale publique non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité digitale publique");
    }
  },

  /**
   * Récupère une entité digitale par UUID avec ses mémos (accès public ou par connaissance de l'UUID)
   */
  async getPublicByUuidWithMemos(uuid: string): Promise<DigitalEntityWithMemos> {
    try {
      return await api.get(`/digitalentity/public/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Entité digitale non trouvée");
      }
      throw new Error("Erreur lors de la récupération de l'entité digitale et ses mémos");
    }
  },

  /**
   * Vérifie si une entité digitale existe par son UUID (public ou privé)
   */
  async checkExists(uuid: string): Promise<AssetExistsResponse> {
    try {
      return await api.get(`/digitalentity/exists/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        return {
          exists: false,
          is_owner: false,
          is_public: false
        };
      }
      throw new Error("Erreur lors de la vérification de l'existence de l'entité digitale");
    }
  }
};

// Exports des fonctions individuelles pour la rétrocompatibilité
export const fetchAllDigitalEntities = (): Promise<DigitalEntity[]> => digitalEntities.fetchAllDigitalEntities();
export const createDigitalEntity = (entity: DigitalEntityCreate): Promise<DigitalEntity> => digitalEntities.createDigitalEntity(entity);
export const updateDigitalEntity = (uuid: string, entity: Partial<DigitalEntityCreate>): Promise<DigitalEntity> => digitalEntities.updateDigitalEntity(uuid, entity);
export const deleteDigitalEntity = (uuid: string): Promise<void> => digitalEntities.deleteDigitalEntity(uuid);
export const fetchDigitalEntityWithMemos = (uuid: string): Promise<DigitalEntityWithMemos> => digitalEntities.fetchDigitalEntityWithMemos(uuid);
export const fetchDigitalEntitiesByUser = (userId: string): Promise<DigitalEntity[]> => digitalEntities.fetchDigitalEntitiesByUser(userId); 